import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Package } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  Button,
  Card,
  Checkbox,
  EmptyState,
  Input,
  Modal,
  Select,
  Skeleton,
  StatusPill,
  Table,
  TextArea,
  useToast,
} from '../components';
import { PageHeader } from '../layouts/PageHeader';
import { useT } from '../i18n';
import type { TranslationKey } from '../i18n';
import type { ProductType, RobotModel } from '../types/api';
import styles from './Catalog.module.css';

/** The two product lines come from the same i18n keys the deal pipeline uses. */
const PRODUCT_TYPE_OPTIONS: { value: ProductType; labelKey: TranslationKey }[] = [
  { value: 'INTEGRATION_PROJECT', labelKey: 'transitions.productTypeIntegration' },
  { value: 'EDUCATIONAL_ROBOT', labelKey: 'transitions.productTypeEducational' },
];

interface CreateModelPayload {
  sku: string;
  name: string;
  description: string;
  basePrice: number;
  productType: ProductType | '';
  active: boolean;
}

const emptyForm: CreateModelPayload = {
  sku: '',
  name: '',
  description: '',
  basePrice: 0,
  productType: '',
  active: true,
};

/** Format a number as USD price. */
function formatPrice(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
}

/** i18n key of a product line — the raw enum code is never rendered. */
function productTypeLabelKey(productType: ProductType): TranslationKey {
  return productType === 'EDUCATIONAL_ROBOT'
    ? 'transitions.productTypeEducational'
    : 'transitions.productTypeIntegration';
}

export function CatalogPage() {
  const { user } = useAuth();
  const { t } = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const isManufacturer = user?.role === 'MANUFACTURER';

  // ── Data ─────────────────────────────────────────────────────────
  const modelsQ = useQuery<RobotModel[]>({
    queryKey: ['robot-models'],
    queryFn: () => api.get<RobotModel[]>('/robot-models'),
  });
  const models = modelsQ.data ?? [];

  // ── Create flow (manufacturer-only) ──────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateModelPayload>(emptyForm);
  const updateField = <K extends keyof CreateModelPayload>(k: K, v: CreateModelPayload[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const createMutation = useMutation({
    mutationFn: (payload: CreateModelPayload) => {
      const clean: Record<string, unknown> = {
        sku: payload.sku.trim(),
        name: payload.name.trim(),
        basePrice: payload.basePrice,
        active: payload.active,
      };
      if (payload.productType) clean.productType = payload.productType;
      if (payload.description.trim()) clean.description = payload.description.trim();
      return api.post<RobotModel>('/robot-models', clean);
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['robot-models'] });
      toast.success(t('catalog.toastCreated'), `${created.sku} — ${created.name}`);
      setCreateOpen(false);
      setForm(emptyForm);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : t('catalog.errorCreate');
      toast.error(t('catalog.toastCreateFail'), msg);
    },
  });

  const submitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (createMutation.isPending) return;
    createMutation.mutate(form);
  };

  const requiredOk =
    form.sku.trim() && form.name.trim() && form.basePrice >= 0 && form.productType;

  const errorMsg = modelsQ.error instanceof Error ? modelsQ.error.message : null;

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('catalog.title')}
        description={
          isManufacturer ? t('catalog.descManufacturer') : t('catalog.descIntegrator')
        }
        action={
          isManufacturer && (
            <Button leftIcon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>
              {t('catalog.add')}
            </Button>
          )
        }
      />

      {errorMsg && (
        <Card className={styles.error}>
          <p>{errorMsg}</p>
        </Card>
      )}

      {modelsQ.isLoading ? (
        <CatalogSkeleton />
      ) : models.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Package size={28} />}
            title={t('catalog.emptyTitle')}
            description={
              isManufacturer
                ? t('catalog.emptyDescManufacturer')
                : t('catalog.emptyDescIntegrator')
            }
            action={
              isManufacturer && (
                <Button size="sm" leftIcon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>
                  {t('catalog.add')}
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>{t('catalog.colSku')}</th>
              <th>{t('catalog.colName')}</th>
              <th>{t('transitions.productTypeLabel')}</th>
              <th>{t('catalog.colDescription')}</th>
              <th className="numeric">{t('catalog.colBasePrice')}</th>
              <th>{t('catalog.colStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {models.map((m) => (
              <tr key={m.id}>
                <td className="mono">{m.sku}</td>
                <td className={styles.name}>{m.name}</td>
                <td>{t(productTypeLabelKey(m.productType))}</td>
                <td className={styles.desc}>
                  {m.description ?? <span className={styles.muted}>—</span>}
                </td>
                <td className="numeric">{formatPrice(m.basePrice)}</td>
                <td>
                  <StatusPill tone={m.active ? 'success' : 'neutral'} subtle={!m.active}>
                    {m.active ? t('common.active') : t('catalog.statusDiscontinued')}
                  </StatusPill>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* ── Create modal (manufacturer-only) ───────────────────── */}
      {isManufacturer && (
        <Modal
          open={createOpen}
          onClose={() => !createMutation.isPending && setCreateOpen(false)}
          title={t('catalog.modalTitle')}
          description={t('catalog.modalDesc')}
          size="md"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => setCreateOpen(false)}
                disabled={createMutation.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                form="create-model-form"
                loading={createMutation.isPending}
                disabled={!requiredOk}
              >
                {t('common.create')}
              </Button>
            </>
          }
        >
          <form id="create-model-form" onSubmit={submitCreate} className={styles.form} noValidate>
            <Input
              label={t('catalog.fieldSku')}
              value={form.sku}
              onChange={(e) => updateField('sku', e.target.value.toUpperCase())}
              placeholder={t('catalog.skuPlaceholder')}
              required
              fullWidth
              autoFocus
            />
            <Input
              label={t('catalog.fieldName')}
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              required
              fullWidth
            />
            <Select
              label={t('transitions.productTypeLabel')}
              value={form.productType}
              onChange={(e) => updateField('productType', e.target.value as ProductType | '')}
              required
              fullWidth
              placeholder={t('transitions.productTypePlaceholder')}
              options={PRODUCT_TYPE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
            />
            <Input
              label={t('catalog.fieldBasePriceUsd')}
              type="number"
              min={0}
              step={100}
              value={String(form.basePrice)}
              onChange={(e) =>
                updateField('basePrice', Number.parseFloat(e.target.value) || 0)
              }
              required
              fullWidth
            />
            <div className={styles.fullWidth}>
              <TextArea
                label={t('catalog.fieldDescription')}
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
                fullWidth
                rows={3}
                placeholder={t('catalog.descriptionPlaceholder')}
              />
            </div>
            <div className={styles.fullWidth}>
              <Checkbox
                label={t('catalog.activeLabel')}
                checked={form.active}
                onChange={(e) => updateField('active', e.target.checked)}
              />
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function CatalogSkeleton() {
  const { t } = useT();
  return (
    <Table>
      <thead>
        <tr>
          <th>{t('catalog.colSku')}</th>
          <th>{t('catalog.colName')}</th>
          <th>{t('transitions.productTypeLabel')}</th>
          <th>{t('catalog.colDescription')}</th>
          <th className="numeric">{t('catalog.colBasePrice')}</th>
          <th>{t('catalog.colStatus')}</th>
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: 4 }).map((_, i) => (
          <tr key={i}>
            {Array.from({ length: 6 }).map((__, j) => (
              <td key={j}><Skeleton width="80%" /></td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
